import { Fragment, useRef, useState } from 'react'
import { AlertCircle, Clock, Crown, Hourglass, PauseCircle, Users } from 'lucide-react'
import TooltipPattern from '@/common/components/TooltipPattern'
import { cn } from '@/common/utils/cn'
import { formatCurrency } from '@/common/utils/formatters'
import { useCountdown } from '../../hooks/useCountdown'
import { INTERACTIVE_CARD } from '../../constants/auctionCard.constant'
import { AuctionCardData, AuctionOriginal, AuctionProposal } from '../../types/auctionCard.type'
import AnimatedCountdown from '../AnimatedCountdown'
import CardLabel from '../CardLabel'

type Props = {
  data: AuctionCardData
}

const AuctionDisputeCard = ({ data }: Props) => {
  const {
    forwardingType,
    metricKind,
    companyName,
    equipmentModel,
    transportType,
    myPosition,
    participants,
    paused,
    endsAt,
    original,
    bestProposal,
    myProposal,
    secondProposal,
    hasSecondProposal,
    isSealed,
    startsAt,
  } = data

  const remaining = useCountdown(endsAt)
  const startsRemaining = useCountdown(startsAt)
  const isLeading = myPosition === 1
  const isDateMetric = metricKind === 'date'

  const participantsRef = useRef<HTMLDivElement>(null)
  const [showParticipants, setShowParticipants] = useState(false)

  const renderValueCell = (
    proposal: { total?: number; discountPercent?: number; estimatedCompletionDate?: string },
    valueClassName: string,
    withDiscountBadge: boolean,
    extraClassName?: string,
  ) => (
    <span className={cn('whitespace-nowrap', extraClassName)}>
      {isDateMetric ? (
        <b className={valueClassName}>{proposal.estimatedCompletionDate ?? '—'}</b>
      ) : (
        <>
          <b className={valueClassName}>{formatCurrency(proposal.total ?? 0)}</b>
          {withDiscountBadge && proposal.discountPercent !== undefined && (
            <span className="ml-1 inline-block align-middle rounded-full bg-success-op px-1.5 py-[1px] text-[9px] font-bold text-valid">
              -{proposal.discountPercent}%
            </span>
          )}
        </>
      )}
    </span>
  )

  const renderSimpleTable = (
    rows: { label: string; proposal?: AuctionProposal | AuctionOriginal; valueClassName: string; badge?: boolean; valueOnly?: boolean; hidden?: boolean }[],
  ) => (
    <div className="bg-white-200 border border-border-default rounded-md p-2">
      <div className="grid grid-cols-[auto_auto_auto_auto] gap-x-3 gap-y-1.5 text-[11px] items-center">
        <span />
        <span className="text-[9px] text-white-300">{isDateMetric ? 'Conclusão' : 'Valor'}</span>
        <span className="text-[9px] text-white-300 text-center">Prazo</span>
        <span className="text-[9px] text-white-300 text-center">Início</span>
        {rows.map((row) => (
          <Fragment key={row.label}>
            <span className="text-[9px] italic text-white-300">{row.label}</span>
            {row.hidden || !row.proposal ? (
              <>
                <span className="flex" title="Proposta oculta">
                  <span className="h-1.5 w-12 rounded-full bg-white-300" />
                </span>
                <span className="flex justify-center" title="Proposta oculta">
                  <span className="h-1.5 w-8 rounded-full bg-white-300" />
                </span>
                <span className="flex justify-center" title="Proposta oculta">
                  <span className="h-1.5 w-8 rounded-full bg-white-300" />
                </span>
              </>
            ) : (
              <>
                {renderValueCell(row.proposal, row.valueClassName, !!row.badge)}
                {row.valueOnly ? (
                  <span />
                ) : (
                  <span className="text-center font-semibold text-white-400">{row.proposal.deadlineDays}d</span>
                )}
                {row.valueOnly ? (
                  <span />
                ) : (
                  <span className="text-center font-semibold text-white-400">{row.proposal.startDate}</span>
                )}
              </>
            )}
          </Fragment>
        ))}
      </div>
    </div>
  )

  const isOngoing = !startsAt || new Date(startsAt).getTime() <= new Date().getTime()
  const notStarted = !isOngoing
  const awaitingInspection = isOngoing && endsAt != null && new Date(endsAt).getTime() <= new Date().getTime()
  const noProposal = isOngoing && !myProposal

  return (
    <div className={cn('relative bg-white-100 border border-border-default rounded-lg flex flex-col shrink-0 overflow-hidden', INTERACTIVE_CARD)}>
      <div
        ref={participantsRef}
        onMouseEnter={() => setShowParticipants(true)}
        onMouseLeave={() => setShowParticipants(false)}
        className="absolute top-2 right-2 z-10 flex items-center gap-1 cursor-default bg-white-200 rounded-full px-2 py-0.5"
      >
        <Users size={12} className="text-white-400" />
        <span className="text-[11px] font-bold text-white-400">{participants}</span>
      </div>

      {showParticipants && (
        <TooltipPattern target={participantsRef.current} expectedWidth={240} expectedHeight={80}>
          <div className="bg-neutral-8 border border-border-default rounded-lg shadow-lg p-3 text-xs flex flex-col gap-1 whitespace-nowrap">
            <p>
              <span className="text-white-300">Estabelecimentos participando: </span>
              <span className="font-bold">{participants}</span>
            </p>
          </div>
        </TooltipPattern>
      )}

      <div className="flex">
        <div className="flex flex-col justify-center items-center text-center gap-1.5 px-2 py-2 border-r border-border-default shrink-0 min-w-[80px]">
          {paused ? (
            <>
              <PauseCircle size={16} className="text-yellow-400" strokeWidth={1.5} />
              <p className="text-[9px] italic leading-tight text-yellow-400 text-center">Leilão pausado</p>
            </>
          ) : awaitingInspection ? (
            <>
              <Hourglass size={16} className="text-primary-blue" strokeWidth={1.5} />
              <p className="text-[9px] italic leading-tight text-primary-blue text-center">Aguardando vistoria</p>
            </>
          ) : notStarted ? (
            <>
              <Clock size={16} className="text-primary-blue" strokeWidth={1.5} />
              <div>
                <p className="text-[9px] italic leading-none text-white-300">Começa em</p>
                <AnimatedCountdown
                  value={startsRemaining}
                  className="text-[10px] font-semibold mt-1 text-white-400 whitespace-nowrap"
                />
              </div>
            </>
          ) : noProposal ? (
            <>
              <AlertCircle size={16} strokeWidth={1.75} className="text-yellow-400" />
              <div>
                <p className="text-[9px] italic leading-none text-white-300">Acaba em</p>
                <AnimatedCountdown
                  value={remaining}
                  className="text-[10px] font-semibold mt-1 text-white-400 whitespace-nowrap"
                />
              </div>
            </>
          ) : (
            <>
              {myPosition != null ? (
                <div className="flex items-center gap-1">
                  {myPosition === 1 && (
                    <Crown size={14} className="text-yellow-400 fill-yellow-400" strokeWidth={1.5} />
                  )}
                  <p className="text-base font-bold leading-none text-white-800">{myPosition}º</p>
                </div>
              ) : (
                <p className="text-[9px] italic leading-tight text-white-300 text-center">Proposta enviada</p>
              )}
              <div>
                <p className="text-[9px] italic leading-none text-white-300">Acaba em</p>
                <AnimatedCountdown
                  value={remaining}
                  className="text-[10px] font-semibold mt-1 text-white-400 whitespace-nowrap"
                />
              </div>
            </>
          )}
        </div>

        <div className="flex flex-col justify-center gap-1 p-3 flex-1 min-w-0">
          <CardLabel
            forwardingType={forwardingType}
            transportType={transportType}
            sealed={!!isSealed}
            className="pr-12"
          />

          <p className="text-[11px] font-semibold text-white-400 truncate" title={companyName}>
            {companyName}
          </p>

          <p className="text-xs font-bold truncate" title={equipmentModel}>{equipmentModel}</p>
        </div>
      </div>

      {notStarted ? (
        !isDateMetric && original && (
          <div className="px-3 pb-3">
            {renderSimpleTable([{ label: 'Original', proposal: original, valueClassName: 'text-white-400', valueOnly: true }])}
          </div>
        )
      ) : noProposal ? (
        <div className="px-3 pb-3 flex flex-col gap-2">
          <span className="text-[10px] italic text-yellow-400">Você ainda não propôs</span>
          {bestProposal ? (
            renderSimpleTable([
              { label: 'Melhor', proposal: bestProposal, valueClassName: 'text-success', badge: true },
              ...(secondProposal
                ? [{ label: '2ª', proposal: secondProposal, valueClassName: 'text-white-800', badge: true }]
                : hasSecondProposal
                  ? [{ label: '2ª', valueClassName: 'text-white-800', hidden: true }]
                  : []),
              ...(!isDateMetric && original ? [{ label: 'Original', proposal: original, valueClassName: 'text-white-400', valueOnly: true }] : []),
            ])
          ) : (
            <div className="rounded-md border border-border-default bg-white-200 p-2 text-center text-[10px] italic text-white-300">
              Sem propostas ainda — seja o primeiro
            </div>
          )}
        </div>
      ) : (
        bestProposal && (
          <div className="px-3 pb-3">
            <div className="bg-white-200 border border-border-default rounded-md p-2">
              <div className="grid grid-cols-[auto_auto_auto_auto] gap-x-3 gap-y-1.5 text-[11px] items-center">
                <span />
                <span className="text-[9px] text-white-300">{isDateMetric ? 'Conclusão' : 'Valor'}</span>
                <span className="text-[9px] text-white-300 text-center">Prazo</span>
                <span className="text-[9px] text-white-300 text-center">Início</span>

                <span className="text-[9px] text-white-400 italic">Melhor{isLeading ? ' (Sua)' : ''}</span>
                {renderValueCell(bestProposal, 'text-success', true)}
                <span className="text-center font-semibold">{bestProposal.deadlineDays}d</span>
                <span className="text-center font-semibold">{bestProposal.startDate}</span>

                {isLeading
                  ? hasSecondProposal && (
                      <>
                        <span className="text-[9px] text-white-400 italic">2ª</span>
                        <span className="flex" title="Proposta oculta">
                          <span className="h-1.5 w-12 rounded-full bg-white-300" />
                        </span>
                        <span className="flex justify-center" title="Proposta oculta">
                          <span className="h-1.5 w-8 rounded-full bg-white-300" />
                        </span>
                        <span className="flex justify-center" title="Proposta oculta">
                          <span className="h-1.5 w-8 rounded-full bg-white-300" />
                        </span>
                      </>
                    )
                  : myProposal && (
                      <>
                        <span className="text-[9px] text-white-400 italic">Sua</span>
                        {renderValueCell(myProposal, 'text-white-800', true)}
                        <span className="text-center font-semibold">{myProposal.deadlineDays}d</span>
                        <span className="text-center font-semibold">{myProposal.startDate}</span>
                      </>
                    )}

                {!isDateMetric && original && (
                  <>
                    <span className="text-[9px] text-white-300 italic">Original</span>
                    {renderValueCell(original, 'text-white-400', false)}
                    <span />
                    <span />
                  </>
                )}
              </div>
            </div>
          </div>
        )
      )}
    </div>
  )
}

export default AuctionDisputeCard
